import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Alert, Button, FormField, Input, Select, Surface } from '@nexvia-logistics/ui';
import { KeyValueListStacked } from '@/shared/KeyValueListStacked';
import { Bank } from '@nexvia-logistics/ui/icons';
import {
  describeBankAccountError,
  getStorageBankAccount,
  listBanks,
  upsertStorageBankAccount,
  type BankCatalogEntry,
  type StorageBankAccount,
  type StorageProfile,
  type UpsertStorageBankAccountInput
} from './storage-api';
import {
  ACCOUNT_TYPE_OPTIONS,
  accountTypeLabel,
  BANK_FIELD_MAX_LENGTH,
  BANK_TRANSFER_PAYMENT_METHOD,
  DOCUMENT_TYPE_OPTIONS,
  documentTypeLabel,
  maskAccountNumber,
  normalizeDigits,
  validateBankAccount,
  type BankAccountFormErrors,
  type BankAccountFormValues
} from './bank-account-fields';

export interface BankAccountFormProps {
  /** A first account starts with the razón social as titular and the RUT as document. */
  profile: Pick<StorageProfile, 'razonSocial' | 'rut'>;
}

type LoadState = 'loading' | 'ready' | 'error';

const NO_ACCOUNT_NOTICE = 'Cargá tu cuenta bancaria para que podamos pagarte.';
const ACCOUNT_NUMBER_HINT = 'Solo los números, sin guiones ni espacios.';

function bankNameOf(account: StorageBankAccount, banks: BankCatalogEntry[]): string {
  if (account.bankName) return account.bankName;
  const fromCatalog = banks.find((bank) => bank.code === account.bankCode)?.name;
  if (fromCatalog) return fromCatalog;
  return account.bankCode ? `Banco ${account.bankCode}` : '—';
}

/** A saved code outside the select's two options (written by something else) starts unpicked, so
 *  the form asks for one instead of re-sending a value no option shows. */
function knownOption(value: string | null | undefined, options: ReadonlyArray<{ value: string }>): string {
  const normalized = (value ?? '').trim().toUpperCase();
  return options.some((option) => option.value === normalized) ? normalized : '';
}

function initialValues(account: StorageBankAccount | null, profile: BankAccountFormProps['profile']): BankAccountFormValues {
  if (!account) {
    return {
      bankCode: '',
      bankAccountType: '',
      accountNumber: '',
      beneficiaryName: profile.razonSocial,
      documentType: 'RUT',
      documentNumber: profile.rut,
      bankEmail: ''
    };
  }
  return {
    bankCode: account.bankCode ?? '',
    bankAccountType: knownOption(account.bankAccountType, ACCOUNT_TYPE_OPTIONS),
    accountNumber: account.accountNumber ?? '',
    beneficiaryName: account.beneficiaryName ?? '',
    documentType: knownOption(account.documentType, DOCUMENT_TYPE_OPTIONS),
    documentNumber: account.documentNumber ?? '',
    bankEmail: account.bankEmail ?? ''
  };
}

function BankAccountSummary({ account, banks }: { account: StorageBankAccount; banks: BankCatalogEntry[] }) {
  const document = [documentTypeLabel(account.documentType), account.documentNumber].filter(Boolean).join(' ');
  const rows = [
    { term: 'Banco', value: bankNameOf(account, banks) },
    { term: 'Tipo de cuenta', value: accountTypeLabel(account.bankAccountType) },
    { term: 'Número de cuenta', value: maskAccountNumber(account.accountNumber) },
    { term: 'Titular', value: account.beneficiaryName || '—' },
    { term: 'Documento', value: document || '—' }
  ];
  if (account.bankEmail) rows.push({ term: 'Correo para avisos del banco', value: account.bankEmail });
  return <KeyValueListStacked items={rows} className="mt-4" />;
}

interface BankAccountEditorProps {
  account: StorageBankAccount | null;
  profile: BankAccountFormProps['profile'];
  banks: BankCatalogEntry[];
  banksFailed: boolean;
  onSaved: (account: StorageBankAccount) => void;
  /** Only when editing a saved account: a first one has nothing to go back to. */
  onCancel?: () => void;
}

/**
 * The form itself, `PATCH /storage/me/bank-account`. Same composition as `QuoteContactForm`:
 * `FormField` + `Input`/`Select` with `invalid` for the critical ring, field errors in `FormField`'s
 * own slot, the API's error in an `Alert` (`describeBankAccountError`).
 */
function BankAccountEditor({ account, profile, banks, banksFailed, onSaved, onCancel }: BankAccountEditorProps) {
  const [values, setValues] = useState<BankAccountFormValues>(() => initialValues(account, profile));
  const [errors, setErrors] = useState<BankAccountFormErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const bankOptions = useMemo(() => banks.map((bank) => ({ value: bank.code, label: bank.name })), [banks]);
  const selectedBank = banks.find((bank) => bank.code === values.bankCode);

  const update = (field: keyof BankAccountFormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setErrorMessage(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const found = validateBankAccount(values);
    if (Object.values(found).some(Boolean)) {
      setErrors(found);
      return;
    }

    const email = values.bankEmail.trim();
    const input: UpsertStorageBankAccountInput = {
      paymentMethod: BANK_TRANSFER_PAYMENT_METHOD,
      bankCode: values.bankCode,
      bankAccountType: values.bankAccountType,
      accountNumber: normalizeDigits(values.accountNumber),
      beneficiaryName: values.beneficiaryName.trim(),
      documentType: values.documentType,
      documentNumber: normalizeDigits(values.documentNumber)
    };
    // Left out, the API keeps the saved email (TypeORM skips `undefined`); '' is what clears it.
    if (email) input.bankEmail = email;
    else if (account?.bankEmail) input.bankEmail = '';

    setSaving(true);
    setErrorMessage(null);
    try {
      const saved = await upsertStorageBankAccount(input);
      // The PATCH answer has no join, so `bankName` is null there: named from the catalog instead.
      onSaved({ ...saved, bankName: saved.bankName ?? selectedBank?.name ?? null });
    } catch (err) {
      setErrorMessage(describeBankAccountError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="mt-4 flex flex-col gap-4" noValidate>
      {banksFailed && (
        <Alert tone="critical">No pudimos cargar la lista de bancos. Recargá la página para intentarlo de nuevo.</Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Banco" error={errors.bankCode}>
          {(field) => (
            <Select
              {...field}
              invalid={Boolean(errors.bankCode)}
              options={bankOptions}
              value={values.bankCode}
              onChange={update('bankCode')}
              placeholder="Elegí tu banco"
            />
          )}
        </FormField>

        <FormField label="Tipo de cuenta" error={errors.bankAccountType}>
          {(field) => (
            <Select
              {...field}
              invalid={Boolean(errors.bankAccountType)}
              options={ACCOUNT_TYPE_OPTIONS}
              value={values.bankAccountType}
              onChange={update('bankAccountType')}
              placeholder="Elegí el tipo de cuenta"
            />
          )}
        </FormField>

        <FormField
          label="Número de cuenta"
          hint={selectedBank?.account_number_hint || ACCOUNT_NUMBER_HINT}
          error={errors.accountNumber}
        >
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(errors.accountNumber)}
              inputMode="numeric"
              autoComplete="off"
              maxLength={BANK_FIELD_MAX_LENGTH.accountNumber}
              value={values.accountNumber}
              onChange={(event) => update('accountNumber')(event.target.value)}
            />
          )}
        </FormField>

        <FormField label="Titular" hint="Como figura en el banco." error={errors.beneficiaryName}>
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(errors.beneficiaryName)}
              autoComplete="off"
              maxLength={BANK_FIELD_MAX_LENGTH.beneficiaryName}
              value={values.beneficiaryName}
              onChange={(event) => update('beneficiaryName')(event.target.value)}
            />
          )}
        </FormField>

        <FormField label="Tipo de documento" error={errors.documentType}>
          {(field) => (
            <Select
              {...field}
              invalid={Boolean(errors.documentType)}
              options={DOCUMENT_TYPE_OPTIONS}
              value={values.documentType}
              onChange={update('documentType')}
              placeholder="Elegí el tipo de documento"
            />
          )}
        </FormField>

        <FormField label="Número de documento" hint="El del titular de la cuenta." error={errors.documentNumber}>
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(errors.documentNumber)}
              inputMode="numeric"
              autoComplete="off"
              maxLength={BANK_FIELD_MAX_LENGTH.documentNumber}
              value={values.documentNumber}
              onChange={(event) => update('documentNumber')(event.target.value)}
            />
          )}
        </FormField>

        <FormField
          label="Correo para avisos del banco (opcional)"
          hint="Si lo cargás, el banco puede avisar ahí cuando te transferimos."
          error={errors.bankEmail}
          className="md:col-span-2"
        >
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(errors.bankEmail)}
              type="email"
              autoComplete="email"
              maxLength={BANK_FIELD_MAX_LENGTH.bankEmail}
              value={values.bankEmail}
              onChange={(event) => update('bankEmail')(event.target.value)}
            />
          )}
        </FormField>
      </div>

      {errorMessage && <Alert tone="critical">{errorMessage}</Alert>}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" size="xs" showArrow={false} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar cuenta bancaria'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" size="xs" showArrow={false} disabled={saving} onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — "Cuenta bancaria" in Mi empresa: where Nexvia pays the
 * depósito what its clients pay for their guardados (the Itaú payout batch reads this row,
 * `storage_bank_accounts`). `GET/PATCH /storage/me/bank-account` through this folder's transport.
 *
 * Loads on its own (not through `useEmpresaData`): a failure here says so in this card and leaves
 * the rest of Mi empresa usable. With no account it says "Cargá tu cuenta bancaria para que podamos
 * pagarte." over the form (titular and document prefilled with the razón social and the RUT); with
 * one, a summary with the number masked to its last four digits and "Editar cuenta bancaria".
 * The bank list is the API's own catalog (`listBanks`), so a bank picked here passes
 * `STORAGE_BANK_CODE_INVALID`. No mock in the Claude Design file draws this card: it follows
 * `QuoteContactForm`/`FiscalDataForm`'s composition.
 */
export function BankAccountForm({ profile }: BankAccountFormProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [account, setAccount] = useState<StorageBankAccount | null>(null);
  const [banks, setBanks] = useState<BankCatalogEntry[]>([]);
  const [banksFailed, setBanksFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoadState('loading');
    const [accountResult, banksResult] = await Promise.allSettled([getStorageBankAccount(), listBanks()]);
    if (accountResult.status === 'rejected') {
      setLoadState('error');
      return;
    }
    setAccount(accountResult.value);
    setBanks(banksResult.status === 'fulfilled' ? banksResult.value : []);
    setBanksFailed(banksResult.status === 'rejected');
    setLoadState('ready');
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSaved = (savedAccount: StorageBankAccount) => {
    setAccount(savedAccount);
    setEditing(false);
    setSaved(true);
  };

  const showEditor = loadState === 'ready' && (editing || !account);

  return (
    <Surface as="section" variant="outlined" padding="md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
          <Bank size={20} className="shrink-0 text-primary" />
          Cuenta bancaria
        </h2>
        {loadState === 'ready' && account && !editing && (
          <Button
            type="button"
            variant="neutral"
            size="xs"
            showArrow={false}
            onClick={() => {
              setSaved(false);
              setEditing(true);
            }}
          >
            Editar cuenta bancaria
          </Button>
        )}
      </div>
      <p className="m-0 mt-1 text-ui-body text-on-surface-variant">Acá te pagamos lo que cobres por tus depósitos.</p>

      {loadState === 'loading' && <p className="m-0 mt-4 text-ui-body text-on-surface-variant">Cargando tu cuenta bancaria…</p>}

      {loadState === 'error' && (
        <div className="mt-4 flex flex-col gap-3">
          <Alert tone="critical">No pudimos cargar tu cuenta bancaria.</Alert>
          <div>
            <Button type="button" variant="outline" size="xs" showArrow={false} onClick={() => void load()}>
              Reintentar
            </Button>
          </div>
        </div>
      )}

      {loadState === 'ready' && !account && (
        <Alert tone="caution" className="mt-4">
          {NO_ACCOUNT_NOTICE}
        </Alert>
      )}

      {loadState === 'ready' && account && !editing && (
        <>
          <BankAccountSummary account={account} banks={banks} />
          {saved && (
            <p role="status" className="m-0 mt-3 text-ui-body text-success-base">
              Cuenta bancaria guardada.
            </p>
          )}
        </>
      )}

      {showEditor && (
        <BankAccountEditor
          account={account}
          profile={profile}
          banks={banks}
          banksFailed={banksFailed}
          onSaved={handleSaved}
          onCancel={account ? () => setEditing(false) : undefined}
        />
      )}
    </Surface>
  );
}
